import { useTranslation } from 'react-i18next';

import { PaymentDeviceRentalStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate } from '@/helpers/paymentDevices';
import { formatFeeAmount } from '@/helpers/paymentDeviceFees';
import { MissingAccessoriesTag } from './DeviceStatusChip';

interface RentalHistoryTableProps {
  rentals: PaymentDeviceRentalDtoIn[];
  /** The first column: the fund (in a device's history) or the device (in a fund's history). */
  by: 'fund' | 'device';
}

const cell = 'py-2 px-2 text-start align-top whitespace-nowrap';

/**
 * Past rentals, one line each (HAT-1434, the office 29/10: «זה הרבה טקסט ולא מסודר בעין»): who or
 * what, out, back, the business days charged and the fee - with a cancelled reservation, a faulty
 * return or missing accessories as a short tag under the name rather than another line of text.
 */
const RentalHistoryTable = ({ rentals, by }: RentalHistoryTableProps) => {
  const { t } = useTranslation();
  const hasFees = rentals.some((rental) => rental.fee);

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-(--border-divider)">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-(--surface-grey-low) text-(--font-body-2)">
          <tr>
            <th className={`${cell} font-semibold`}>{t(by === 'fund' ? 'paymentDevices.history.fund' : 'paymentDevices.history.device')}</th>
            <th className={`${cell} font-semibold`}>{t('paymentDevices.history.handedOver')}</th>
            <th className={`${cell} font-semibold`}>{t('paymentDevices.history.returned')}</th>
            {hasFees && <th className={`${cell} font-semibold`}>{t('paymentDevices.history.days')}</th>}
            {hasFees && <th className={`${cell} font-semibold`}>{t('paymentDevices.history.fee')}</th>}
          </tr>
        </thead>
        <tbody>
          {rentals.map((rental) => {
            const cancelled = rental.status === PaymentDeviceRentalStatus.Cancelled;
            return (
              <tr key={rental.id} className="border-t border-(--border-divider)">
                <td className={cell}>
                  <span className="flex flex-col items-start gap-1">
                    <span className="body-sm-semibold">
                      {by === 'fund'
                        ? `${rental.fundId} | ${rental.fundName ?? ''}`
                        : <bdi>{rental.deviceNumber}</bdi>}
                    </span>
                    {(cancelled || rental.returnedFaulty || (rental.missingAccessories?.length ?? 0) > 0) && (
                      <span className="flex flex-wrap gap-1">
                        {cancelled && (
                          <span className="rounded-full bg-(--surface-grey-low) px-2 text-xs text-(--font-body-2)">
                            {t('paymentDevices.history.cancelled')}
                          </span>
                        )}
                        {rental.returnedFaulty && (
                          <span className="rounded-full bg-red-50 px-2 text-xs text-red-600">{t('paymentDevices.blade.returnedFaulty')}</span>
                        )}
                        <MissingAccessoriesTag items={rental.missingAccessories} />
                      </span>
                    )}
                  </span>
                </td>
                <td className={cell}>{formatDeviceDate(rental.handedOverDate) || '—'}</td>
                <td className={cell}>{formatDeviceDate(rental.returnedDate) || '—'}</td>
                {hasFees && <td className={cell}>{rental.fee ? rental.fee.accruedDays : '—'}</td>}
                {hasFees && <td className={cell}>{rental.fee ? formatFeeAmount(rental.fee.accruedAmount) : '—'}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RentalHistoryTable;
